/* ============================================================
   ET - ARTICLE — table of contents, scroll-spy, mobile sticky
   contents bar, recommended-product rotator and the mobile USP
   slider.

   The table of contents is generated at runtime because the body
   is merchant HTML from the Shopify article editor: Liquid cannot
   walk it, so the headings are found in the DOM instead, given
   ids, and mirrored into every contents shell the section
   rendered (the in-flow card and the fixed mobile bar).

   Everything is scoped to [data-et-article-body] — never to the
   document — so headings in the header panel, the sidebar, the
   USP cards, the theme header and the footer can never leak into
   the list.
   ============================================================ */

(function () {
  'use strict';

  /* The section emits one script tag per instance; only the first should bind. */
  if (window.etArticleLoaded) return;
  window.etArticleLoaded = true;

  var ROOT_SELECTOR = '.et-article';
  var MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  var OPEN_MS = 380;
  var CLOSE_MS = 300;
  var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  function qsa(scope, selector) {
    return Array.prototype.slice.call(scope.querySelectorAll(selector));
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ------------------------------------------------------------------ *
   * Heading ids
   * ------------------------------------------------------------------ */

  /* Dutch headings are full of accented characters ("Zó droog je een dekbed"),
     which would otherwise all collapse to the same run of dashes and force the
     numeric de-duplication below to do the work an anchor should do itself. */
  function slugify(text) {
    var value = String(text || '').trim();

    if (typeof value.normalize === 'function') {
      value = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    value = value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return value;
  }

  /* A merchant can paste the same heading twice, and the body is not the only
     thing on the page with ids — check the whole document, not just the list
     built so far. */
  function uniqueId(base) {
    /* The fallback has to be applied BEFORE the loop as well as inside it: a
       heading that slugifies to nothing — one that is only an emoji, or only
       punctuation — would otherwise collide and then produce the id "-2", which
       is not a valid identifier to hand to getElementById or a fragment link. */
    var root = base || 'section';
    var candidate = root;
    var suffix = 2;

    while (document.getElementById(candidate)) {
      candidate = root + '-' + suffix;
      suffix += 1;
    }

    return candidate;
  }

  function collectHeadings(body) {
    /* A heading inside an injected content block is part of that CARD, not part of
       the article's structure — "Zo kies je de juiste maat" on a callout is not a
       section of the post. Listing it in the contents would send the reader to a
       decorative element, so blocks are skipped. Headings the merchant typed
       directly in the body are collected exactly as before. */
    var headings = qsa(body, 'h2, h3').filter(function (heading) {
      return !heading.closest('[data-et-article-block]');
    });
    var out = [];

    headings.forEach(function (heading) {
      var text = (heading.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) return;

      /* A heading the merchant already anchored keeps its own id, so links
         published elsewhere on the site keep resolving. */
      if (!heading.id) {
        heading.id = uniqueId(slugify(text));
      }

      out.push({
        id: heading.id,
        text: text,
        level: heading.tagName === 'H3' ? 3 : 2,
        el: heading
      });
    });

    return out;
  }

  /* ------------------------------------------------------------------ *
   * Contents lists
   * ------------------------------------------------------------------ */

  function buildList(list, headings) {
    /* 'toc' (in-flow card) or 'stoc' (fixed bar) — the two shells use different
       BEM blocks, so the prefix travels on the hook rather than being guessed
       from the element's class list. */
    var prefix = 'et-article__' + (list.getAttribute('data-et-article-toc-list') || 'toc');
    var fragment = document.createDocumentFragment();

    headings.forEach(function (heading, index) {
      var item = document.createElement('li');
      item.className = prefix + '-item ' + prefix + '-item--' + heading.level;

      var link = document.createElement('a');
      link.className = prefix + '-link';
      link.href = '#' + heading.id;
      link.setAttribute('data-et-article-toc-target', heading.id);

      var num = document.createElement('span');
      num.className = prefix + '-num';
      num.setAttribute('aria-hidden', 'true');
      /* Two digits: the design draws 01-05 and a bare "1" would break the
         22px number column's alignment. */
      num.textContent = (index + 1 < 10 ? '0' : '') + (index + 1);

      var text = document.createElement('span');
      text.className = prefix + '-text';
      text.textContent = heading.text;

      link.appendChild(num);
      link.appendChild(text);
      item.appendChild(link);
      fragment.appendChild(item);
    });

    list.textContent = '';
    list.appendChild(fragment);
  }

  /* ------------------------------------------------------------------ *
   * Measured-height expand / collapse
   *
   * Animating the real height rather than a max-height guess: a max-height
   * transition spends its whole duration on a distance the content never uses,
   * and silently clips anything past the guess.
   * ------------------------------------------------------------------ */

  function clearPending(panel) {
    if (panel.__etArticleEnd) {
      panel.removeEventListener('transitionend', panel.__etArticleEnd);
      panel.__etArticleEnd = null;
    }
    if (panel.__etArticleTimer) {
      clearTimeout(panel.__etArticleTimer);
      panel.__etArticleTimer = null;
    }
  }

  function settleOpen(panel) {
    /* Back to auto so the panel keeps working if its content reflows — a font
       swap or a rotation both change how many lines a heading takes. */
    panel.style.height = 'auto';
    panel.style.overflow = '';
    panel.style.transition = '';
  }

  /* `reveal` is what takes the resting collapsed class off. It runs AFTER the
     start height has been measured — removing it first would restore height:auto
     and make the start and the target identical, which is a transition from a
     value to itself: no animation at all, the panel just appears. */
  function openPanel(panel, reveal) {
    clearPending(panel);

    var start = panel.offsetHeight;
    if (reveal) reveal();

    if (prefersReducedMotion()) {
      settleOpen(panel);
      return;
    }

    var target = panel.scrollHeight;

    panel.style.overflow = 'hidden';
    /* `transition: none` and not the shorthand `''`: the stylesheet's own
       `transition: visibility 0s linear .3s` must not survive into the open, or
       the panel stays invisible for the whole animation. */
    panel.style.transition = 'none';
    panel.style.visibility = 'visible';
    panel.style.height = start + 'px';
    void panel.offsetHeight; /* flush, so the next height change transitions instead of jumping */
    panel.style.transition = 'height ' + OPEN_MS + 'ms ' + EASE;
    panel.style.height = target + 'px';

    var done = function (event) {
      if (event && event.target !== panel) return;
      clearPending(panel);
      settleOpen(panel);
      panel.style.visibility = '';
    };

    panel.__etArticleEnd = done;
    panel.addEventListener('transitionend', done);
    /* transitionend is skipped when the tab is backgrounded mid-animation. */
    panel.__etArticleTimer = setTimeout(done, OPEN_MS + 80);
  }

  /* `conceal` adds the resting collapsed class, and runs only once the height
     has finished animating — the class carries the delayed `visibility: hidden`
     that takes the links out of the tab order, and applying it up front would
     blank the panel before it had shrunk. */
  function closePanel(panel, conceal) {
    clearPending(panel);

    if (prefersReducedMotion()) {
      panel.style.height = '';
      panel.style.overflow = '';
      panel.style.transition = '';
      panel.style.visibility = '';
      if (conceal) conceal();
      return;
    }

    /* From `auto` there is nothing to interpolate — pin the measured height
       first, then collapse. */
    panel.style.overflow = 'hidden';
    panel.style.transition = 'none';
    panel.style.visibility = 'visible';
    panel.style.height = panel.scrollHeight + 'px';
    void panel.offsetHeight;
    panel.style.transition = 'height ' + CLOSE_MS + 'ms ' + EASE;
    panel.style.height = '0px';

    var done = function (event) {
      if (event && event.target !== panel) return;
      clearPending(panel);
      /* Hand the resting state back to the stylesheet rather than leaving inline
         values behind, so an interrupted transition still lands closed — and so
         the stylesheet's visibility rule can take over from here. */
      panel.style.transition = '';
      panel.style.height = '';
      panel.style.visibility = '';
      if (conceal) conceal();
    };

    panel.__etArticleEnd = done;
    panel.addEventListener('transitionend', done);
    panel.__etArticleTimer = setTimeout(done, CLOSE_MS + 80);
  }

  /* ------------------------------------------------------------------ *
   * Scroll offset
   * ------------------------------------------------------------------ */

  /* Impact already sets html { scroll-padding-block-start: sticky-area + 20px }
     so native anchor jumps clear the sticky header. Scroll-spy has to use the
     same line, or the highlighted item and the heading under the header
     disagree by exactly the header's height. */
  var cachedOffset = null;

  function scrollOffset() {
    if (cachedOffset !== null) return cachedOffset;

    var value = 0;

    try {
      value = parseFloat(window.getComputedStyle(document.documentElement).scrollPaddingTop);
    } catch (e) {}

    if (!value || isNaN(value)) value = 100;

    /* Cached because scroll-spy asks for it every animation frame, and reading
       a computed style there forces a style recalculation mid-scroll. It only
       changes when the header does, so a resize is enough to invalidate it. */
    cachedOffset = value;
    return cachedOffset;
  }

  function resetScrollOffset() {
    cachedOffset = null;
  }

  /* ------------------------------------------------------------------ *
   * Recommended-product rotator
   * ------------------------------------------------------------------ */

  function initRecommended(root) {
    var rec = root.querySelector('[data-et-article-rec]');
    if (!rec || rec.__etArticleRec) return;

    var viewport = rec.querySelector('[data-et-article-rec-viewport]');
    var slides = qsa(rec, '[data-et-article-rec-slide]');
    if (!viewport || slides.length === 0) return;

    rec.__etArticleRec = true;

    var prev = rec.querySelector('[data-et-article-rec-prev]');
    var next = rec.querySelector('[data-et-article-rec-next]');
    var thumb = rec.querySelector('[data-et-article-rec-thumb]');
    var ticking = false;

    function maxScroll() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    /* The carousel is a native scroll container, so its own scroll position is
       the single source of truth — a separate index would drift the moment the
       reader swiped, tabbed into an off-screen link, or resized. */
    function sync() {
      ticking = false;

      /* Read BEFORE anything is disabled. Setting `disabled` on the element that
         currently holds focus blurs it synchronously, so by the time the state
         is written document.activeElement is already <body> and the hand-off
         below would never recognise which arrow the reader had been using. */
      var focused = document.activeElement;

      var limit = maxScroll();
      var atStart = viewport.scrollLeft <= 1;
      var atEnd = limit === 0 || viewport.scrollLeft >= limit - 1;

      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;

      /* An arrow that disables itself while it still holds focus drops the
         keyboard user on the document body, halfway down a long article. Hand
         focus to the arrow that is still live. */
      if (prev && next) {
        if (focused === next && next.disabled && !prev.disabled) prev.focus();
        else if (focused === prev && prev.disabled && !next.disabled) next.focus();
      }

      if (thumb) {
        var rail = thumb.parentNode;
        var style = window.getComputedStyle(rail);
        var inner =
          rail.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0);
        var travel = Math.max(0, inner - thumb.offsetWidth);
        var progress = limit > 0 ? viewport.scrollLeft / limit : 0;
        thumb.style.transform = 'translateX(' + travel * progress + 'px)';
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(sync);
    }

    function step(direction) {
      var width = viewport.clientWidth;
      if (!width) return;

      var index = Math.round(viewport.scrollLeft / width) + direction;
      var target = Math.max(0, Math.min(maxScroll(), index * width));

      if (viewport.scrollTo) {
        viewport.scrollTo({ left: target, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      } else {
        viewport.scrollLeft = target;
      }
    }

    if (prev) {
      prev.addEventListener('click', function () {
        step(-1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        step(1);
      });
    }

    viewport.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    /* Impact's ProductForm fires cart:error ON THE FORM when /cart/add.js refuses
       — the last unit sold while the article was open, most often. Only
       <buy-buttons> listens for it, and a sidebar widget has no business
       rendering one, so without this the button would simply do nothing and look
       broken. Delegated in CAPTURE: cart:error does not bubble. */
    rec.addEventListener(
      'cart:error',
      function (event) {
        var form = event.target.closest ? event.target.closest('form') : null;
        if (!form) return;

        var box = form.querySelector('[data-et-article-rec-error]');
        if (!box) return;

        var message = (event.detail && event.detail.error) || '';
        box.textContent = message;
        box.hidden = !message;
      },
      true
    );

    /* A successful add clears whatever the last failure left behind. */
    document.addEventListener('cart:change', function () {
      qsa(rec, '[data-et-article-rec-error]').forEach(function (box) {
        box.textContent = '';
        box.hidden = true;
      });
    });

    sync();

    /* Handed back so a theme-editor section reload does not leave a resize
       listener pointed at a detached carousel. */
    rec.__etArticleRecTeardown = function () {
      window.removeEventListener('resize', onScroll);
      viewport.removeEventListener('scroll', onScroll);
    };
  }

  /* ------------------------------------------------------------------ *
   * Mobile USP slider
   * ------------------------------------------------------------------ */

  /* In the theme editor a section can be injected after the deferred bundle has
     already run, and on a cold first paint the bundle may not have executed
     yet — cover both without assuming an order. */
  function whenSwiperReady(cb) {
    if (window.Swiper) {
      cb();
      return;
    }

    var vendor = document.querySelector('script[src*="et-swiper.min.js"]');
    if (vendor) {
      vendor.addEventListener('load', cb, { once: true });
      return;
    }

    var tries = 0;
    var timer = setInterval(function () {
      if (window.Swiper) {
        clearInterval(timer);
        cb();
      } else if (++tries > 100) {
        clearInterval(timer);
      }
    }, 50);
  }

  function createUspSwiper(root) {
    var usps = root.querySelector('[data-et-article-usps]');
    if (!usps || usps.etSwiper) return;

    var el = usps.querySelector('[data-et-article-usp-swiper]');
    if (!el) return;

    whenSwiperReady(function () {
      /* The viewport can leave the mobile range, or the section can be removed,
         while the vendor bundle is still loading — re-check rather than
         instantiate into a stale state. */
      if (usps.etSwiper || !usps.isConnected || !MOBILE_QUERY.matches) return;

      var options = {
        /* Slide width lives in CSS so the card keeps the 310px the design draws
           and the peek of the next one comes out of the 32px gap. */
        slidesPerView: 'auto',
        spaceBetween: 32,
        speed: 450,
        grabCursor: true,
        watchOverflow: true,
        threshold: 4,
        observer: true,
        observeParents: true,
        a11y: Object.assign({ enabled: true }, window.etSwiperA11y)
      };

      var scrollbar = usps.querySelector('[data-et-article-usp-scrollbar]');
      if (scrollbar) {
        /* Progress pill: the design draws a fixed 48px thumb in a 111px track,
           so the drag size is pinned rather than left on 'auto' (which would
           size it to the visible-slide ratio and shrink as cards are added). */
        options.scrollbar = { el: scrollbar, draggable: true, dragSize: 48, hide: false };
      }

      usps.etSwiper = new window.Swiper(el, options);
    });
  }

  function destroyUspSwiper(scope) {
    var node = scope || document;
    var kill = function (usps) {
      if (usps.etSwiper) {
        usps.etSwiper.destroy(true, true);
        usps.etSwiper = null;
      }
    };

    if (node.matches && node.matches('[data-et-article-usps]')) kill(node);
    qsa(node, '[data-et-article-usps]').forEach(kill);
  }

  function syncUspSwiper(root) {
    if (MOBILE_QUERY.matches) {
      createUspSwiper(root);
    } else {
      destroyUspSwiper(root);
    }
  }

  /* ------------------------------------------------------------------ *
   * Content components
   * ------------------------------------------------------------------ */

  /* The merchant pastes a callout as a wrapper class around ordinary editor
     output — an image, a heading, a paragraph, a list. Only that one class has
     to be right; this sorts whatever is inside into the media half and the text
     half the stylesheet lays out, so the paste does not have to match a rigid
     shape and cannot be broken by the editor reformatting it.

     Everything here is additive: with the script disabled the same content is
     still a stacked card, because the CSS base is the column. */
  function normaliseCallouts(body) {
    qsa(body, '.et-callout').forEach(function (callout) {
      if (callout.dataset.etCalloutReady === '1') return;
      callout.dataset.etCalloutReady = '1';

      var media = callout.querySelector('.et-callout__media');

      if (!media) {
        var image = callout.querySelector('img, picture');

        if (image) {
          media = document.createElement('div');
          media.className = 'et-callout__media';

          /* Shopify's editor usually leaves a pasted image wrapped in its own
             <p>. Lift the image out of that wrapper and drop the wrapper if
             nothing else was in it, so the media half is the image alone. */
          var host = image;
          while (host.parentNode && host.parentNode !== callout) {
            host = host.parentNode;
          }

          callout.insertBefore(media, host);
          media.appendChild(image);

          if (host !== image && host.parentNode === callout && !host.textContent.replace(/\s+/g, '')) {
            callout.removeChild(host);
          }
        }
      }

      if (callout.querySelector('.et-callout__body')) return;

      /* Everything that is not the media becomes the text half — collected into
         an array first, because appendChild moves nodes and would otherwise
         mutate the live HTMLCollection mid-loop. */
      var rest = Array.prototype.filter.call(callout.children, function (child) {
        return child !== media;
      });

      if (rest.length === 0) return;

      var text = document.createElement('div');
      text.className = 'et-callout__body';
      rest.forEach(function (child) {
        text.appendChild(child);
      });
      callout.appendChild(text);
    });
  }

  /* Ja / Nee in a comparison cell become the design's tick and cross. Matched on
     the cell's WHOLE text, so a sentence that merely contains the word is left
     alone, and the original word stays in the DOM for screen readers — the glyph
     is drawn on a span that is visually replaced, not on text that is deleted. */
  var COMPARE_YES = ['ja', 'yes', 'oui', 'y', '✓', '✔'];
  var COMPARE_NO = ['nee', 'no', 'non', 'n', '✗', '✘', 'x', '–', '-', '—'];

  function markComparisons(body) {
    qsa(body, '.et-compare td').forEach(function (cell) {
      if (cell.dataset.etCompareMarked === '1') return;

      /* Never the label column: "Ja" could legitimately be a row's name. */
      if (cell.cellIndex === 0) return;

      var text = (cell.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      var yes = COMPARE_YES.indexOf(text) > -1;
      var no = COMPARE_NO.indexOf(text) > -1;
      if (!yes && !no) return;

      cell.dataset.etCompareMarked = '1';

      var mark = document.createElement('span');
      mark.className = 'et-compare__mark' + (no ? ' et-compare__mark--no' : '');
      /* The word itself is the accessible name — the mask is decoration. */
      mark.textContent = cell.textContent.trim();

      cell.textContent = '';
      cell.appendChild(mark);
    });
  }

  /* Mobile comparison (Figma 1898:15196) needs three things markup alone
     cannot give: the intro ABOVE the scrolling box, a "scroll" hint in the
     sticky header cell, and a bar that follows the scroll. All three are added
     once and shown only by the mobile stylesheet; the intro copies the card's
     text, and each viewport displays exactly one of the two, so a screen reader
     never hears it twice. Runs after wrapTables, which provides the scroller. */
  var COMPARE_HINTS = {
    nl: 'Scroll om meer te ontdekken',
    en: 'Scroll to see more',
    fr: 'Faites défiler pour en voir plus',
    de: 'Scrollen für mehr'
  };

  function enhanceComparisons(body) {
    var cleanups = [];

    qsa(body, '.et-compare').forEach(function (table) {
      if (table.dataset.etCompareEnhanced === '1') return;
      table.dataset.etCompareEnhanced = '1';

      var wrap = table.parentNode;
      var scroller = wrap && wrap.classList && wrap.classList.contains('et-article__table-scroll') ? wrap : null;
      var first = table.querySelector('thead th:first-child');

      if (first) {
        var parts = Array.prototype.slice.call(first.children);

        if (parts.length > 0 && scroller && scroller.parentNode) {
          var intro = document.createElement('div');
          intro.className = 'et-compare__intro';

          parts.forEach(function (part, index) {
            var line = document.createElement('p');
            line.className = index === 0 ? 'et-compare__intro-title' : 'et-compare__intro-text';
            line.textContent = part.textContent.replace(/\s+/g, ' ').trim();
            intro.appendChild(line);
          });

          scroller.parentNode.insertBefore(intro, scroller);
        }

        var hint = document.createElement('span');
        var lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
        hint.className = 'et-compare__hint';
        hint.setAttribute('aria-hidden', 'true');
        hint.textContent = COMPARE_HINTS[lang] || COMPARE_HINTS.en;
        first.appendChild(hint);
      }

      var footCell = table.querySelector('tfoot tr > :first-child');
      if (!footCell || !scroller) return;

      var track = document.createElement('span');
      track.className = 'et-compare__progress';
      track.setAttribute('aria-hidden', 'true');
      track.appendChild(document.createElement('span'));
      footCell.appendChild(track);

      /* 111px track, 2px inset each side, 48px thumb: 59px of travel. */
      function sync() {
        var max = scroller.scrollWidth - scroller.clientWidth;
        var ratio = max > 0 ? Math.min(1, Math.max(0, Math.abs(scroller.scrollLeft) / max)) : 0;
        table.style.setProperty('--et-compare-progress', Math.round(ratio * 59) + 'px');
      }

      scroller.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();

      cleanups.push(function () {
        scroller.removeEventListener('scroll', sync);
        window.removeEventListener('resize', sync);
      });
    });

    return cleanups.length > 0
      ? function () {
          cleanups.forEach(function (cleanup) {
            cleanup();
          });
        }
      : null;
  }

  /* ------------------------------------------------------------------ *
   * Content component: the product upsell row (Figma 1898:14922)
   * ------------------------------------------------------------------ */

  var UPSELL_LABELS = {
    nl: { prev: 'Vorige producten', next: 'Volgende producten' },
    en: { prev: 'Previous products', next: 'Next products' },
    fr: { prev: 'Produits précédents', next: 'Produits suivants' },
    de: { prev: 'Vorherige Produkte', next: 'Weitere Produkte' }
  };

  var UPSELL_ARROW =
    '<svg class="et-article__arrow-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" ' +
    'xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M14.7803 6.21967C15.0732 6.51257 15.0732 6.98743 14.7803 7.28033L10.0607 12L14.7803 ' +
    '16.7197C15.0732 17.0125 15.0732 17.4875 14.7803 17.7803C14.4875 18.0732 14.0125 18.0732 ' +
    '13.7197 17.7803L8.46967 12.5303C8.17678 12.2375 8.17678 11.7626 8.46967 11.4697L13.7197 ' +
    '6.21967C14.0125 5.92678 14.4875 5.92678 14.7803 6.21967Z" fill="currentColor"/></svg>';

  /* The row scrolls natively, so this only ADDS the arrows and the progress bar,
     and only when the cards actually overflow. With the script off — or on a
     phone, where the controls are hidden — the strip still swipes. */
  function initUpsells(body) {
    var cleanups = [];

    qsa(body, '.et-upsell').forEach(function (row) {
      if (row.dataset.etUpsellReady === '1') return;

      var cards = row.querySelectorAll('.et-upsell__card');
      if (cards.length < 2) return;

      row.dataset.etUpsellReady = '1';

      /* --- colour swatches -------------------------------------------------
         The merchant pastes plain spans, because a span is what survives the
         rich text editor untouched. Each one is upgraded to a real button here
         so it can be clicked, tabbed to and announced. A swatch carrying
         data-image swaps the card photo; data-color rewrites the colour name.
         A swatch with neither still selects, it just has nothing to change. */
      qsa(row, '.et-upsell__swatches').forEach(function (group) {
        var card = group.closest('.et-upsell__card');
        if (!card) return;

        var photo = card.querySelector('img');
        var name = card.querySelector('.et-upsell__color b');
        var swatches = [];

        Array.prototype.slice.call(group.children).forEach(function (node) {
          if (node.tagName === 'BUTTON') { swatches.push(node); return; }
          if (node.tagName !== 'SPAN') return;

          var swap = document.createElement('button');
          swap.type = 'button';
          swap.className = node.className;
          swap.setAttribute('style', node.getAttribute('style') || '');
          Object.keys(node.dataset).forEach(function (key) {
            swap.dataset[key] = node.dataset[key];
          });
          /* The colour name is the accessible name; without one the button
             would announce as an unlabelled control. */
          var swatchName = node.dataset.color || node.getAttribute('title');
          if (swatchName) swap.setAttribute('aria-label', swatchName);
          node.parentNode.replaceChild(swap, node);
          swatches.push(swap);
        });

        if (swatches.length < 2) return;

        function select(chosen) {
          swatches.forEach(function (swatch) {
            var on = swatch === chosen;
            swatch.classList.toggle('is-selected', on);
            swatch.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          if (photo && chosen.dataset.image) photo.src = chosen.dataset.image;
          if (name && chosen.dataset.color) name.textContent = chosen.dataset.color;
        }

        swatches.forEach(function (swatch) {
          swatch.setAttribute('aria-pressed', 'false');
          function onPick() { select(swatch); }
          swatch.addEventListener('click', onPick);
          cleanups.push(function () { swatch.removeEventListener('click', onPick); });
        });

        select(swatches[0]);
      });

      var lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
      var labels = UPSELL_LABELS[lang] || { prev: 'Previous products', next: 'Next products' };

      var controls = document.createElement('div');
      controls.className = 'et-upsell__controls';

      var track = document.createElement('span');
      track.className = 'et-upsell__progress';
      track.setAttribute('aria-hidden', 'true');
      track.appendChild(document.createElement('span'));

      var nav = document.createElement('div');
      nav.className = 'et-upsell__nav';

      function button(kind, label) {
        var el = document.createElement('button');
        el.type = 'button';
        el.className = 'et-upsell__arrow et-upsell__arrow--' + kind;
        el.setAttribute('aria-label', label);
        el.innerHTML = UPSELL_ARROW;
        return el;
      }

      var prev = button('prev', labels.prev);
      var next = button('next', labels.next);
      nav.appendChild(prev);
      nav.appendChild(next);
      controls.appendChild(track);
      controls.appendChild(nav);
      row.parentNode.insertBefore(controls, row.nextSibling);

      function step() {
        var card = row.querySelector('.et-upsell__card');
        if (!card) return row.clientWidth;
        var gap = parseFloat(getComputedStyle(row).columnGap) || 16;
        return card.getBoundingClientRect().width + gap;
      }

      /* 128px track, 48px thumb: 80px of travel. */
      function sync() {
        var max = row.scrollWidth - row.clientWidth;
        var at = Math.abs(row.scrollLeft);
        var ratio = max > 0 ? Math.min(1, Math.max(0, at / max)) : 0;
        row.style.setProperty('--et-upsell-progress', Math.round(ratio * 80) + 'px');
        prev.disabled = at <= 1;
        next.disabled = at >= max - 1;
      }

      function go(direction) {
        /* Read focus BEFORE the button can be disabled — the browser blurs a
           disabled button synchronously and the hand-off would read null. */
        var active = document.activeElement;
        row.scrollBy({ left: direction * step(), behavior: 'smooth' });
        window.setTimeout(function () {
          if (active === prev && prev.disabled) next.focus();
          if (active === next && next.disabled) prev.focus();
        }, 320);
      }

      function onPrev() { go(-1); }
      function onNext() { go(1); }

      prev.addEventListener('click', onPrev);
      next.addEventListener('click', onNext);
      row.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();

      cleanups.push(function () {
        prev.removeEventListener('click', onPrev);
        next.removeEventListener('click', onNext);
        row.removeEventListener('scroll', sync);
        window.removeEventListener('resize', sync);
        if (controls.parentNode) controls.parentNode.removeChild(controls);
        row.dataset.etUpsellReady = '';
      });
    });

    return cleanups.length > 0
      ? function () {
          cleanups.forEach(function (cleanup) {
            cleanup();
          });
        }
      : null;
  }

  /* ------------------------------------------------------------------ *
   * Tables
   * ------------------------------------------------------------------ */

  /* Impact forces table { width: 100% } and ships no scroll container, so a
     wide merchant table would push the whole two-column grid open. */
  function wrapTables(body) {
    var wraps = [];

    qsa(body, 'table').forEach(function (table) {
      var parent = table.parentNode;
      if (!parent) return;

      var wrap;
      if (parent.classList && parent.classList.contains('et-article__table-scroll')) {
        wrap = parent;
      } else {
        wrap = document.createElement('div');
        wrap.className = 'et-article__table-scroll';
        parent.insertBefore(wrap, table);
        wrap.appendChild(table);
      }

      wraps.push(wrap);
    });

    /* The tab stop is added ONLY while the table actually overflows. An
       unconditional tabindex puts a stop on every table for a container that
       cannot scroll, and role="region" without a name is an unlabelled landmark
       — so neither is emitted until the scroll is real. */
    function syncAffordance() {
      wraps.forEach(function (wrap) {
        if (wrap.scrollWidth > wrap.clientWidth + 1) {
          wrap.setAttribute('tabindex', '0');

          /* A group needs a name, and the only name available is the merchant's
             own <caption> — the theme carries no locale key for "table", and a
             hard-coded English one would be wrong on a Dutch store. With no
             caption the wrapper stays a plain focusable scroller, which is
             still keyboard-operable and beats an unlabelled landmark. */
          var caption = wrap.querySelector('caption');
          var label = caption ? caption.textContent.replace(/\s+/g, ' ').trim() : '';

          if (label) {
            wrap.setAttribute('role', 'group');
            wrap.setAttribute('aria-label', label);
          }
        } else {
          wrap.removeAttribute('tabindex');
          wrap.removeAttribute('role');
          wrap.removeAttribute('aria-label');
        }
      });
    }

    if (wraps.length === 0) return null;

    syncAffordance();
    window.addEventListener('resize', syncAffordance, { passive: true });

    /* A table's width is not final at DOMContentLoaded: an image in a cell or a
       late webfont can push it past its wrapper afterwards, and the affordance
       would then be missing from the one table that actually needs it. */
    window.addEventListener('load', syncAffordance);
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(syncAffordance);
    }

    return function () {
      window.removeEventListener('resize', syncAffordance);
      window.removeEventListener('load', syncAffordance);
    };
  }

  /* ------------------------------------------------------------------ *
   * Section
   * ------------------------------------------------------------------ */

  /* A sidebar whose only card is the contents, on an article that turned out to
     have no headings, would hold a 437px column open beside the body for
     nothing. */
  function collapseEmptySidebar(root) {
    var sidebar = root.querySelector('.et-article__sidebar');
    if (!sidebar) return;

    var hasVisible = Array.prototype.some.call(sidebar.children, function (child) {
      return !child.hasAttribute('hidden');
    });
    if (hasVisible) return;

    sidebar.hidden = true;
    var layout = root.querySelector('.et-article__layout');
    if (layout) layout.classList.add('et-article__layout--no-sidebar');
  }

  function initArticle(root) {
    if (!root || root.__etArticleInit) return;
    root.__etArticleInit = true;

    var teardowns = [];

    initRecommended(root);
    syncUspSwiper(root);

    var body = root.querySelector('[data-et-article-body]');
    if (body) {
      normaliseCallouts(body);
      markComparisons(body);

      var untangleTables = wrapTables(body);
      if (untangleTables) teardowns.push(untangleTables);

      var unenhanceComparisons = enhanceComparisons(body);
      if (unenhanceComparisons) teardowns.push(unenhanceComparisons);

      var unbuildUpsells = initUpsells(body);
      if (unbuildUpsells) teardowns.push(unbuildUpsells);
    }

    var toc = root.querySelector('[data-et-article-toc]');
    var stoc = root.querySelector('[data-et-article-stoc]');
    var lists = qsa(root, '[data-et-article-toc-list]');
    var headings = body ? collectHeadings(body) : [];

    /* Registered BEFORE any early return: the USP slider is created and
       destroyed on this event, and an article with no h2/h3 — or none at all —
       still has USP cards that must switch modes when the viewport crosses
       750px. Binding it after the contents bail-out left them frozen in
       whichever mode the page first loaded in. */
    function onBreakpointChange() {
      syncUspSwiper(root);
      if (root.__etArticleTocBreakpoint) root.__etArticleTocBreakpoint();
    }

    if (typeof MOBILE_QUERY.addEventListener === 'function') {
      MOBILE_QUERY.addEventListener('change', onBreakpointChange);
    } else if (typeof MOBILE_QUERY.addListener === 'function') {
      MOBILE_QUERY.addListener(onBreakpointChange);
    }

    teardowns.push(function () {
      if (typeof MOBILE_QUERY.removeEventListener === 'function') {
        MOBILE_QUERY.removeEventListener('change', onBreakpointChange);
      } else if (typeof MOBILE_QUERY.removeListener === 'function') {
        MOBILE_QUERY.removeListener(onBreakpointChange);
      }
    });

    root.__etArticleTeardown = function () {
      teardowns.forEach(function (fn) {
        fn();
      });
      teardowns.length = 0;
    };

    /* A post with no h2/h3 gets no contents at all rather than an empty card —
       both shells stay `hidden`, which Impact enforces with
       [hidden] { display: none !important }. */
    if (!body || lists.length === 0 || headings.length === 0) {
      collapseEmptySidebar(root);
      return;
    }

    lists.forEach(function (list) {
      buildList(list, headings);
    });

    if (toc) toc.removeAttribute('hidden');
    if (stoc) stoc.removeAttribute('hidden');

    var links = qsa(root, '[data-et-article-toc-target]');
    var tocHeader = toc ? toc.querySelector('[data-et-article-toc-header]') : null;
    var tocToggle = toc ? toc.querySelector('[data-et-article-toc-toggle]') : null;
    var tocPanel = toc ? toc.querySelector('[data-et-article-toc-panel]') : null;
    var stocToggle = stoc ? stoc.querySelector('[data-et-article-stoc-toggle]') : null;
    var stocPanel = stoc ? stoc.querySelector('[data-et-article-stoc-panel]') : null;

    /* ---- In-flow card: an accordion on mobile, always open on desktop ---- */

    function syncTocMode() {
      if (!toc || !tocToggle || !tocPanel) return;
      if (MOBILE_QUERY.matches) return;

      /* Crossing back to desktop has to undo whatever the mobile accordion left
         behind: above 750px the stylesheet removes the toggle entirely and the
         card is always open, so any collapsed state has to be unwound here or
         the contents would simply be gone. */
      toc.classList.remove('is-collapsed');
      clearPending(tocPanel);
      tocPanel.style.height = '';
      tocPanel.style.overflow = '';
      tocPanel.style.transition = '';
      tocPanel.style.visibility = '';
      tocToggle.setAttribute('aria-expanded', 'true');
    }

    /* One handler on the heading row, not on the button: the row is the tap
       target the design draws, and the button's own activation — mouse, Enter
       or Space — bubbles straight into it, so pointer and keyboard take one
       path and cannot double-toggle. */
    if (tocHeader && tocToggle && tocPanel) {
      tocHeader.addEventListener('click', function (event) {
        if (!MOBILE_QUERY.matches) return;

        /* Let a real link or control in the row do its own thing. */
        if (event.target.closest && event.target.closest('a, input, select, textarea')) return;

        /* And do not collapse the card out from under someone who was selecting
           the label text. */
        var selection = window.getSelection && window.getSelection();
        if (selection && String(selection) && tocHeader.contains(selection.anchorNode)) return;

        var willOpen = tocToggle.getAttribute('aria-expanded') !== 'true';
        tocToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');

        if (willOpen) {
          /* The class has to come off BEFORE the measurement, because it is what
             holds height:0 — but openPanel reads the start height itself, so it
             is removed inside the open path rather than here. */
          openPanel(tocPanel, function () {
            toc.classList.remove('is-collapsed');
          });
        } else {
          closePanel(tocPanel, function () {
            toc.classList.add('is-collapsed');
          });
        }
      });
    }

    syncTocMode();

    /* ---- Fixed bar ---- */

    function closeSticky() {
      if (!stoc || !stocToggle || !stocPanel) return;
      if (stocToggle.getAttribute('aria-expanded') !== 'true') return;
      stocToggle.setAttribute('aria-expanded', 'false');
      closePanel(stocPanel, function () {
        stoc.classList.remove('is-expanded');
      });
    }

    if (stocToggle && stocPanel) {
      stocToggle.addEventListener('click', function () {
        var willOpen = stocToggle.getAttribute('aria-expanded') !== 'true';

        /* Only the OPEN branch writes the attribute here. closeSticky() returns early unless it is
           still 'true', so pre-writing 'false' made every second tap a no-op and the panel could
           never be closed again. */
        if (willOpen) {
          stocToggle.setAttribute('aria-expanded', 'true');
          openPanel(stocPanel, function () {
            stoc.classList.add('is-expanded');
          });
        } else {
          closeSticky();
        }
      });
    }

    /* Tapping an entry jumps to the section and gets the panel out of the way.
       The anchor's own navigation is left alone: it updates the URL, feeds the
       back button, and lands on the right line because Impact already sets
       html { scroll-padding-block-start }. */
    links.forEach(function (link) {
      link.addEventListener('click', function () {
        closeSticky();
      });
    });

    /* ---- Scroll-spy + bar visibility ---- */

    var ticking = false;
    var activeId = null;
    var stickyVisible = null;

    function measure() {
      ticking = false;

      var offset = scrollOffset() + 8;
      var current = headings[0];

      for (var i = 0; i < headings.length; i++) {
        var rect = headings[i].el.getBoundingClientRect();

        /* A heading inside a closed <details> or a display:none block reports an
           all-zero rect, and top 0 always satisfies the test below — it would
           win scroll-spy outright from anywhere on the page. Skip it. */
        if (rect.width === 0 && rect.height === 0) continue;

        if (rect.top <= offset) {
          current = headings[i];
        } else {
          break;
        }
      }

      if (current && current.id !== activeId) {
        activeId = current.id;
        links.forEach(function (link) {
          link.classList.toggle('is-active', link.getAttribute('data-et-article-toc-target') === activeId);
        });
      }

      if (stoc && toc) {
        /* Show once the in-flow card is fully behind the reader, and hide again
           as soon as the end of the article comes into view — so the bar never
           covers the last thing they are reading and no scroll spacer is
           needed underneath it. */
        var cardGone = toc.getBoundingClientRect().bottom < 0;
        var stillReading = body.getBoundingClientRect().bottom > window.innerHeight;
        var visible = MOBILE_QUERY.matches && cardGone && stillReading;

        if (visible !== stickyVisible) {
          stickyVisible = visible;
          stoc.classList.toggle('is-visible', visible);
          stoc.setAttribute('aria-hidden', visible ? 'false' : 'true');
          if (!visible) closeSticky();
        }
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }

    function onResize() {
      resetScrollOffset();
      onScroll();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    /* The section's single breakpoint listener lives above and calls this. */
    root.__etArticleTocBreakpoint = function () {
      syncTocMode();
      /* The bar only exists below 750px; leaving the previous verdict cached
         would strand it visible after a rotation into landscape. */
      stickyVisible = null;
      measure();
    };

    /* Set the initial state synchronously: a reader landing mid-article on a
       shared anchor must not see the bar flash in on their first scroll. */
    measure();

    teardowns.push(function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      root.__etArticleTocBreakpoint = null;
    });
  }

  /* shopify:section:load hands us the section wrapper, and the schema's
     `class: et-article` puts the root class on that very wrapper — so the scope
     itself has to be tested, not only its descendants. */
  function each(scope, fn) {
    var node = scope || document;
    if (node.matches && node.matches(ROOT_SELECTOR)) fn(node);
    qsa(node, ROOT_SELECTOR).forEach(fn);
  }

  function boot(scope) {
    each(scope, initArticle);
  }

  function teardown(scope) {
    each(scope, function (root) {
      if (root.__etArticleTeardown) {
        root.__etArticleTeardown();
        root.__etArticleTeardown = null;
      }

      root.__etArticleTocBreakpoint = null;

      var rec = root.querySelector('[data-et-article-rec]');
      if (rec) {
        if (rec.__etArticleRecTeardown) {
          rec.__etArticleRecTeardown();
          rec.__etArticleRecTeardown = null;
        }
        /* Clearing the init flag is what actually lets the carousel come back.
           Without it initRecommended early-returns on the next
           shopify:section:load and the arrows, the progress pill and the scroll
           sync are all dead for the rest of the theme-editor session. */
        rec.__etArticleRec = false;
      }

      destroyUspSwiper(root);
      root.__etArticleInit = false;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });

  /* Bring the USP card the merchant just selected in the theme editor into
     view — only meaningful while the mobile slider is actually running. */
  document.addEventListener('shopify:block:select', function (event) {
    var usps = event.target.closest ? event.target.closest('[data-et-article-usps]') : null;
    if (!usps || !usps.etSwiper) return;

    var card = event.target.closest('.et-article__usp');
    if (!card) return;

    var index = qsa(usps, '.et-article__usp').indexOf(card);
    if (index > -1) usps.etSwiper.slideTo(index, 400);
  });
})();
